/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/role-has-required-aria-props */
import { useMemo, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import cn from 'classnames';

import { SelectChip } from './SelectChip';
import { SelectOption } from './SelectOption';
import { SelectGroupOption } from './SelectGroupOption';
import { useSelectInput } from './useSelectInput';

/**
 * Homebrew multiselect — drop-in replacement for multiselect-react-dropdown.
 *
 * Props:
 *   name            string
 *   options         [{id, value, title, city_neighbors?}]
 *   initialSelected [{id, value, title}]  — pre-selected (edit mode)
 *   onSelect        (updatedItems) => void
 *   onRemove        (updatedItems) => void
 *   placeholder     string
 *   isMulti         bool   — checkboxes, multiple values
 *   isSearchable    bool   — filter input shown when open
 *   isGrouped       bool   — city-neighbor grouped mode
 *   hasError        bool   — red bottom border
 *   isBoxed         bool   — full rounded border instead of the bottom underline
 *   isDisabled      bool
 *   clearOnSelect   bool   — single mode: fire onSelect but keep the input empty (pick-and-forget)
 *   isResetClicked  bool   — parent signals reset (same pattern as all other inputs)
 *   setIsResetClicked fn   — called after reset so parent can clear the flag
 */
export const SelectInput = ({
  options = [],
  initialSelected = [],
  onSelect,
  onRemove,
  placeholder = '',
  isMulti = false,
  isSearchable = false,
  isGrouped = false,
  hasError = false,
  isBoxed = false,
  isDisabled = false,
  clearOnSelect = false,
  isResetClicked = false,
  setIsResetClicked,
}) => {
  const {
    isOpen,
    toggleOpen,
    closeDropdown,
    searchQuery,
    setSearchQuery,
    selectedItems,
    setSelectedItems,
    filteredOptions,
    isSelected,
    toggleItem,
    rootRef,
  } = useSelectInput({
    initialSelectedItems: initialSelected,
    options,
    isSearchable,
    isResetClicked,
    onRemove,
    onReset: () => setIsResetClicked?.(false),
  });

  const searchInputRef = useRef(null);

  const sortedEntries = useMemo(() => {
    if (!isGrouped) return null;
    const groups = filteredOptions.filter(
      o => Array.isArray(o.city_neighbors) && o.city_neighbors.length >= 1
    );
    const groupChildIds = new Set(
      groups.flatMap(g => g.city_neighbors.map(n => n.id))
    );
    const flatOptions = filteredOptions.filter(
      o => !groups.some(g => g.id === o.id) && !groupChildIds.has(o.id)
    );
    return [
      ...groups.map(g => ({ ...g, type: 'group' })),
      ...flatOptions.map(o => ({ ...o, type: 'flat' })),
    ].sort((a, b) => a.title.localeCompare(b.title));
  }, [isGrouped, filteredOptions]);
  const handleToggleOpen = () => {
    if (isDisabled) return;
    const opening = !isOpen;
    toggleOpen();
    if (opening && isSearchable) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Escape') closeDropdown();
  };

  const handleOptionClick = item => {
    if (!isMulti) {
      const updated = [item];
      if (!clearOnSelect) setSelectedItems(updated);
      onSelect?.(updated);
      closeDropdown();
      return;
    }
    const updated = toggleItem(item);
    setSelectedItems(updated);
    if (isSelected(item)) {
      onRemove?.(updated);
    } else {
      onSelect?.(updated);
    }
  };

  const handleChipRemove = item => {
    const updated = selectedItems.filter(
      s => s.value !== item.value && !(item.id != null && s.id === item.id)
    );
    setSelectedItems(updated);
    onRemove?.(updated);
  };

  const handleGroupHeaderClick = (_group, children, allSelected) => {
    let updated;
    if (allSelected) {
      const childIds = new Set(children.map(c => c.id));
      updated = selectedItems.filter(
        s => !childIds.has(s.id) && !childIds.has(s.value)
      );
    } else {
      const toAdd = children.filter(
        c => !selectedItems.some(s => s.value === c.value || s.id === c.id)
      );
      updated = [...selectedItems, ...toAdd];
    }
    setSelectedItems(updated);
    onSelect?.(updated);
  };

  const showPlaceholder = selectedItems.length === 0;

  return (
    <div
      ref={rootRef}
      className="relative w-full"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {/* Input bar */}
      <div
        onClick={handleToggleOpen}
        className={cn(
          'flex items-center cursor-pointer',
          isBoxed
            ? [
              'h-11 md:h-10 rounded-md border bg-white px-2',
              hasError ? 'border-redColor' : 'border-gray-300',
            ]
            : 'h-[37px] min-h-[37px] max-h-[37px]',
          { 'opacity-50 pointer-events-none': isDisabled }
        )}
        style={
          isBoxed
            ? undefined
            : {
              borderBottom: hasError
                ? '1px solid rgb(232 72 85)'
                : '1px solid rgb(229 231 235)',
            }
        }
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {/* Chips + placeholder */}
        <div className="flex flex-1 items-center flex-nowrap overflow-x-auto hide-scroll gap-1 min-w-0 pr-1">
          {selectedItems.map(item => (
            <SelectChip
              key={item.value ?? item.id}
              item={item}
              onRemove={handleChipRemove}
            />
          ))}

          {showPlaceholder && !isSearchable && (
            <span className="text-sm text-gray-400 pl-1 whitespace-nowrap select-none">
              {placeholder}
            </span>
          )}

          {isSearchable && isOpen ? (
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onClick={e => e.stopPropagation()}
              placeholder={showPlaceholder ? placeholder : ''}
              className="flex-1 min-w-[60px] text-sm bg-transparent outline-none border-none pl-1"
            />
          ) : isSearchable && showPlaceholder ? (
            <span className="text-sm text-gray-400 pl-1 whitespace-nowrap select-none">
              {placeholder}
            </span>
          ) : null}
        </div>

        {/* Arrow */}
        <div className="shrink-0 flex items-center pl-1 pr-1">
          <ChevronDown
            size={14}
            color="#111926"
            className={cn('transition-transform duration-150', {
              'rotate-180': isOpen,
            })}
          />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <ul
          role="listbox"
          className="absolute top-full left-0 w-full min-w-[150px] bg-white border border-gray-200 shadow-sm z-[50] max-h-60 overflow-y-auto"
        >
          {isGrouped ? (
            <>
              {sortedEntries.map(entry =>
                entry.type === 'group' ? (
                  <SelectGroupOption
                    key={entry.id}
                    group={entry}
                    selectedItems={selectedItems}
                    onGroupHeaderClick={handleGroupHeaderClick}
                    onChildClick={handleOptionClick}
                  />
                ) : (
                  <SelectOption
                    key={entry.value ?? entry.id}
                    item={entry}
                    isSelected={isSelected(entry)}
                    isMulti={isMulti}
                    onClick={handleOptionClick}
                  />
                )
              )}
            </>
          ) : (
            filteredOptions.map(opt => (
              <SelectOption
                key={opt.value ?? opt.id}
                item={opt}
                isSelected={isSelected(opt)}
                isMulti={isMulti}
                onClick={handleOptionClick}
              />
            ))
          )}

          {filteredOptions.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-400">Nothing found</li>
          )}
        </ul>
      )}
    </div>
  );
};
